// HqViewModeToggle — operator-controlled Executive ↔ Workspace switch.
//
// Per Harrison's Living HQ vision (closing note):
//   Executive View (default) — cinematic headquarters with ambient
//     backdrop, AI employees, department accents. The wow factor.
//   Workspace View — environment fades to almost nothing so long
//     work sessions stay comfortable and fast.
//
// Persistence: localStorage.hq.viewMode = 'executive' | 'workspace'.
// One-click chip that lives in the shell chrome (mounted above the
// top bar). The chip is small enough to disappear into the chrome
// but visible enough that any operator finds it in ~2 seconds.
//
// Under the hood it drives the existing HqEnvironment mode:
//   executive → 'department'  (rich, current default)
//   workspace → 'focus'       (backdrop pushed back, heavy blur)

import React, { useEffect } from 'react';
import hq from './theme';
import { useHqEnv, HQ_MODES } from './HqEnvironment';

const LS_KEY = 'hq.viewMode';

export default function HqViewModeToggle() {
  const { mode, setMode } = useHqEnv();

  // On first mount, restore the operator's saved preference
  useEffect(() => {
    let saved = null;
    try { saved = localStorage.getItem(LS_KEY); } catch {}
    if (saved === 'workspace')      setMode(HQ_MODES.FOCUS);
    else if (saved === 'executive') setMode(HQ_MODES.DEPARTMENT);
    // No default write — first-time operators get the default (executive)
    // via the provider's initial state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isWorkspace = mode === HQ_MODES.FOCUS;
  const toggle = () => {
    const next = isWorkspace ? HQ_MODES.DEPARTMENT : HQ_MODES.FOCUS;
    setMode(next);
    try {
      localStorage.setItem(LS_KEY, next === HQ_MODES.FOCUS ? 'workspace' : 'executive');
    } catch {}
  };

  return (
    <button
      onClick={toggle}
      aria-label={`Switch to ${isWorkspace ? 'Executive' : 'Workspace'} view`}
      title={isWorkspace
        ? 'Currently in Workspace view — click to return to Executive view'
        : 'Currently in Executive view — click to switch to Workspace view for deep-focus work'}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '4px 12px', borderRadius: 999,
        background: 'rgba(5,7,9,0.55)',
        border: `1px solid ${hq.color.line}`,
        color: hq.color.textSec,
        fontFamily: hq.type.mono, fontSize: 10, letterSpacing: '.14em',
        cursor: 'pointer',
        transition: 'all 180ms ease',
        backdropFilter: 'blur(4px)',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = hq.color.amber; e.currentTarget.style.color = hq.color.amber; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = hq.color.line; e.currentTarget.style.color = hq.color.textSec; }}
    >
      <span aria-hidden style={{
        fontSize: 12,
        color: hq.color.amber,
        transition: 'transform 240ms ease',
        transform: isWorkspace ? 'rotate(180deg)' : 'none',
      }}>◐</span>
      <span>{isWorkspace ? 'WORKSPACE' : 'EXECUTIVE'}</span>
    </button>
  );
}
